import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Send, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Icon, Button, IconButton, TextField, Select, Switch, Badge, Panel, PageHead, etSyd } from "@/sentinel";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_OPTS = DAYS.map((d) => ({ value: d, label: d }));
const HOUR_OPTS = Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, "0")}:00` }));

/** The schedule is stored in UTC; show what that hour is in ET and Sydney today. */
const utcHourLocal = (h) => {
  const n = new Date();
  return etSyd(new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate(), Number(h) || 0)), { seconds: false });
};

/** System notice (neutral family): configuration, not an error. */
function Notice({ children, ...rest }) {
  return (
    <div {...rest} className="sn-row sn-small" style={{
      flexWrap: "nowrap", alignItems: "flex-start", gap: "var(--space-2)", padding: "var(--space-2) var(--space-3)",
      borderRadius: "var(--radius-sm)", background: "var(--notice-dim)", border: "1px solid var(--border-strong)", color: "var(--text-primary)",
    }}>
      <Icon name="Info" size={14} style={{ marginTop: 1, flex: "none", color: "var(--notice)" }} />
      <span>{children}</span>
    </div>
  );
}

export default function Digest() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canEmail = can("analyst");
  const [showEmail, setShowEmail] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);

  const { data } = useQuery({ queryKey: ["pm-digest"], queryFn: api.pmDigest });
  const { data: emailStatus } = useQuery({ queryKey: ["pm-digest-email-status"], queryFn: api.pmDigestEmailStatus });
  const { data: schedule } = useQuery({ queryKey: ["pm-digest-schedule"], queryFn: api.pmDigestSchedule });
  const md = data?.markdown || "";
  const configured = !!emailStatus?.configured;

  const [schEnabled, setSchEnabled] = useState(false);
  const [schDay, setSchDay] = useState("Mon");
  const [schHour, setSchHour] = useState(13);
  const [schRecipients, setSchRecipients] = useState("");
  useEffect(() => {
    if (schedule) {
      setSchEnabled(!!schedule.enabled);
      setSchDay(schedule.day || "Mon");
      setSchHour(schedule.hour_utc ?? 13);
      setSchRecipients((schedule.recipients || []).join(", "));
    }
  }, [schedule]);

  const saveSchedule = async () => {
    try {
      const list = schRecipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      await api.pmSetDigestSchedule({ enabled: schEnabled, day: schDay, hour_utc: Number(schHour), recipients: list });
      toast.success(schEnabled ? `Auto-send set for ${schDay} ${String(schHour).padStart(2, "0")}:00 UTC.` : "Auto-send disabled.");
      qc.invalidateQueries({ queryKey: ["pm-digest-schedule"] });
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    }
  };

  const send = async () => {
    const list = recipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one recipient email."); return; }
    setSending(true);
    try {
      const r = await api.pmDigestEmail({ recipients: list });
      toast.success(`Digest emailed to ${r.recipients.length} recipient(s).`);
      setShowEmail(false); setRecipients("");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-digest">
      <PageHead
        crumb={["Project", "Weekly digest"]}
        title="Weekly digest"
        context="Client-facing summary of progress, decisions and spend."
        actions={
          <>
            <Button icon="Copy" onClick={() => { navigator.clipboard.writeText(md); toast.success("Copied to clipboard."); }} data-testid="pm-digest-copy">Copy</Button>
            {canEmail && (
              <Button variant="primary" icon={Mail} aria-expanded={showEmail} onClick={() => setShowEmail((v) => !v)}
                title={configured ? "Email this digest" : "Email is not configured yet"} data-testid="pm-digest-email">Email digest</Button>
            )}
          </>
        }
      />

      {showEmail && (
        <Panel title="Email this digest" icon={Mail} data-testid="pm-digest-email-panel"
          actions={<IconButton icon="X" label="Close email panel" size="sm" onClick={() => setShowEmail(false)} />}>
          <div className="sn-stack">
            {!configured && (
              <Notice data-testid="pm-digest-email-notice">
                Email delivery isn't active yet. Add a Resend API key to <code className="sn-num">backend/.env</code> to enable sending. You can still copy the digest above.
              </Notice>
            )}
            <div className="sn-row" style={{ alignItems: "flex-end", flexWrap: "nowrap" }}>
              <TextField label="Recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="recipient@example.com, another@example.com"
                className="flex-1" data-testid="pm-digest-recipients" />
              <Button variant="primary" icon={Send} onClick={send} loading={sending} disabled={!configured} data-testid="pm-digest-send">{sending ? "Sending…" : "Send"}</Button>
            </div>
            {configured && emailStatus?.sender && <span className="sn-help">From: {emailStatus.sender}</span>}
          </div>
        </Panel>
      )}

      <Panel title="Preview" icon="Newspaper" data-testid="pm-digest-preview">
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, lineHeight: "20px", color: "var(--text-primary)" }}>{md || "Loading digest…"}</pre>
      </Panel>

      {canEmail && (
        <Panel title="Auto-send schedule" icon={CalendarClock} data-testid="pm-digest-schedule"
          meta={
            <Badge icon={schEnabled ? "Check" : "Minus"} data-testid="pm-digest-schedule-state">
              {schEnabled ? `ON · ${schDay} ${String(schHour).padStart(2, "0")}:00 UTC` : "OFF"}
            </Badge>
          }>
          <div className="sn-stack">
            <p className="sn-small sn-muted" style={{ margin: 0 }}>
              Email the digest automatically once a week (UTC). {schedule?.last_sent && <>Last auto-send: <span className="sn-num">{schedule.last_sent}</span> ({schedule.last_result}). </>}
            </p>
            {!configured && <Notice>Add a Resend key to activate. The schedule is saved regardless.</Notice>}
            <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
              <Switch label="Auto-send enabled" checked={schEnabled} onChange={(v) => setSchEnabled(v)} data-testid="pm-digest-schedule-enabled" />
              <Select label="Day" options={DAY_OPTS} value={schDay} onChange={(v) => setSchDay(v)} width={96} data-testid="pm-digest-schedule-day" />
              <Select label="Hour (UTC)" options={HOUR_OPTS} value={schHour} onChange={(v) => setSchHour(Number(v))} width={112} data-testid="pm-digest-schedule-hour" />
              <TextField label="Recipients" value={schRecipients} onChange={(e) => setSchRecipients(e.target.value)} placeholder="a@x.com, b@y.com"
                className="flex-1" style={{ minWidth: 200 }} data-testid="pm-digest-schedule-recipients" />
              <Button variant="primary" onClick={saveSchedule} data-testid="pm-digest-schedule-save">Save</Button>
            </div>
            <span className="sn-help"><span className="sn-num">{String(schHour).padStart(2, "0")}:00 UTC</span> is <span className="sn-num">{utcHourLocal(schHour)}</span> today.</span>
          </div>
        </Panel>
      )}
    </div>
  );
}
